import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Button,
  Divider,
  Row,
  Col,
  Input,
  Form,
  InputNumber,
  Checkbox,
  Radio,
  Select,
  message,
} from 'antd';
import api from '@services/api';
import { useTranslation } from 'react-i18next';
import SelectCustomers from '@components/SelectCustomers';
import useSwr from '@hooks/useSwr';
import { SharedPlanPayload } from '@pages/Administrative/SharedPlans/NewSharedPlanModal';
import CustomTag from '@components/CustomTag';
import { bytesFormat, toBytes } from '@utils/formatters';
import * as S from './styles';

const { Item } = Form;

export interface GroupingPayload {
  id: number;
  name: string;
  automaticBlocking: boolean;
  departmentId: number;
  franchiseBytes: number;
  sharingType: 1 | 2; // 'Individual' | 'Shared'
  sharingPlanId?: number;
}

interface NewGroupingModalProps {
  open: boolean;
  onClose: () => void;
  mutateData: (data: GroupingPayload) => void;
}

const NewGroupingModal: React.FC<NewGroupingModalProps> = ({
  open,
  onClose,
  mutateData,
}) => {
  const { t } = useTranslation(['groupings', 'attributes']);
  const [form] = Form.useForm<GroupingPayload>();
  const sharingType = Form.useWatch('sharingType', form);
  const departmentId = Form.useWatch('departmentId', form);
  const sharingPlanId = Form.useWatch('sharingPlanId', form);

  const [saveLoading, setSaveLoading] = useState(false);
  const [franchiseUnit, setFranchiseUnit] = useState<'KB' | 'MB' | 'GB'>('MB');

  const createGrouping = useCallback(
    (payload: GroupingPayload) =>
      api.post(`/service-proxy/broker/inventory-groups`, payload),
    [],
  );

  const { data } = useSwr<{ content: SharedPlanPayload[] }>(
    departmentId && sharingType === 2
      ? `/service-proxy/broker/sharing-plans`
      : null,
    {
      params: {
        linesPerPage: 1000,
        departmentId,
      },
    },
  );

  const sharedPlans = useMemo(() => data?.content, [data?.content]);

  const sharedPlansOptions = useMemo(
    () =>
      sharedPlans?.map(sharedPlan => ({
        label: `${sharedPlan?.id} - ${sharedPlan?.description}`,
        value: sharedPlan?.id,
      })),
    [sharedPlans],
  );

  const departmentWithoutSharedPlans = useMemo(() => {
    const isShared = sharingType === 2;
    const hasNoSharedPlans = sharedPlans && sharedPlansOptions?.length === 0;

    return isShared && hasNoSharedPlans;
  }, [sharedPlans, sharedPlansOptions?.length, sharingType]);

  useEffect(() => {
    if (departmentWithoutSharedPlans) {
      form.validateFields(['departmentId']);
    } else {
      form.setFields([
        {
          name: 'departmentId',
          errors: [],
        },
      ]);
    }
  }, [departmentWithoutSharedPlans, form]);

  const selectedSharedPlan = useMemo(
    (): SharedPlanPayload | undefined =>
      Array.isArray(sharedPlans)
        ? sharedPlans?.find(sharedPlan => sharedPlan.id === sharingPlanId)
        : undefined,
    [sharedPlans, sharingPlanId],
  );

  useEffect(() => {
    if (selectedSharedPlan) {
      form.setFieldsValue({
        automaticBlocking: selectedSharedPlan.automaticBlocking,
      });
    }
  }, [form, selectedSharedPlan]);

  const handleOnClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  const handleFormFinish = useCallback(
    async (payload: GroupingPayload) => {
      setSaveLoading(true);
      try {
        const payloadWithBytes = {
          ...payload,
          franchiseBytes: toBytes(
            Number(payload.franchiseBytes),
            franchiseUnit,
          ),
        };
        await createGrouping(payloadWithBytes);
        mutateData(payload);
        message.success(t('msgs.groupingCreatedSuccessfully'));
        handleOnClose();
      } catch {
        message.error(t('msgs.errorCreatingGrouping'));
      }
      setSaveLoading(false);
    },
    [franchiseUnit, createGrouping, mutateData, t, handleOnClose],
  );

  return (
    <S.ModalContainer
      bodyStyle={{ padding: '24px 40px' }}
      width={500}
      title={t('newGrouping')}
      open={open}
      onCancel={handleOnClose}
      destroyOnClose
      footer={null}
    >
      <Form
        form={form}
        scrollToFirstError
        onFinish={handleFormFinish}
        layout="vertical"
        initialValues={{ sharingType: 1 }}
      >
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Item label={t('sharingType.title')} name="sharingType">
              <Radio.Group
                optionType="button"
                defaultValue={1}
                options={[1, 2].map(item => ({
                  label: t(`sharingType.${item}`),
                  value: item,
                }))}
              />
            </Item>
          </Col>

          <Col span={24}>
            <Item
              label={t('attributes:description')}
              name="name"
              rules={[
                {
                  required: true,
                  message: t('msgs.fillThisField'),
                },
              ]}
            >
              <Input />
            </Item>
          </Col>

          <Col span={24}>
            <Item
              label={t('attributes:department')}
              name="departmentId"
              rules={[
                {
                  required: true,
                  message: t('msgs.selectAnOption'),
                },
                {
                  validator: () =>
                    departmentWithoutSharedPlans
                      ? Promise.reject(
                          new Error(
                            `* ${t('msgs.departmentWithoutSharedPlans')}`,
                          ),
                        )
                      : Promise.resolve(),
                },
              ]}
            >
              <SelectCustomers
                isMotBusiness
                onChange={value => {
                  form.setFieldsValue({
                    departmentId: value,
                    sharingPlanId: undefined,
                  });
                }}
              />
            </Item>
          </Col>

          {sharingType === 1 ? (
            <>
              <Col sm={12} xs={24}>
                <Item
                  label={t('attributes:franchise')}
                  name="franchiseBytes"
                  rules={[
                    {
                      required: true,
                      message: t('msgs.fillThisField'),
                    },
                  ]}
                >
                  <InputNumber
                    addonAfter={
                      <Select
                        value={franchiseUnit}
                        onChange={setFranchiseUnit}
                        options={['KB', 'MB', 'GB'].map(item => ({
                          label: item,
                          value: item,
                        }))}
                        style={{ width: 64 }}
                      />
                    }
                    min={0}
                    defaultValue={0}
                  />
                </Item>
              </Col>
              <Col
                sm={12}
                xs={24}
                style={{
                  display: 'flex',
                  justifyContent: 'end',
                  alignItems: 'center',
                }}
              >
                <Item
                  name="automaticBlocking"
                  valuePropName="checked"
                  style={{ margin: 0 }}
                >
                  <Checkbox>{t('attributes:automaticBlocking.title')}</Checkbox>
                </Item>
              </Col>
            </>
          ) : (
            <>
              {departmentId && (
                <S.SharedPlanContent>
                  <Item
                    label={t('attributes:sharedPlan')}
                    name="sharingPlanId"
                    rules={[
                      {
                        required: true,
                        message: t('msgs.selectAnOption'),
                      },
                    ]}
                    style={{ margin: 0 }}
                  >
                    <Select
                      options={sharedPlansOptions}
                      style={{ width: '100%' }}
                      loading={!sharedPlans}
                      disabled={sharedPlansOptions?.length === 0}
                    />
                  </Item>

                  {selectedSharedPlan && (
                    <div className="shared-plan-content-tags">
                      <CustomTag>
                        {`${t('attributes:franchise')}: ${bytesFormat(
                          selectedSharedPlan?.franchiseBytes,
                        )}`}
                      </CustomTag>
                      <CustomTag>
                        {`${t('attributes:automaticBlocking.title')}: ${t(
                          `attributes:automaticBlocking.${
                            selectedSharedPlan?.automaticBlocking
                              ? 'activated'
                              : 'disabled'
                          }`,
                        ).toUpperCase()}`}
                      </CustomTag>
                    </div>
                  )}
                </S.SharedPlanContent>
              )}
            </>
          )}
        </Row>
        <Divider />

        <Row justify="end" gutter={[8, 8]}>
          <Button loading={saveLoading} type="primary" htmlType="submit">
            {t('buttons.confirm')}
          </Button>
        </Row>
      </Form>
    </S.ModalContainer>
  );
};

export default NewGroupingModal;
